import { useState } from "react";
import type {
  CCAMActe,
  InsuranceNetwork,
  TherapeuticActKind,
  TherapeuticChoiceAct,
  TherapeuticChoiceCombination,
  TherapeuticChoiceRowKey,
} from "cadendar-shared";
import {
  canKindBeAddedToRow,
  getChoiceKindsForRow,
  getTherapeuticMaterialFamilyForActKind,
  isRequiredKindForRow,
  therapeuticActKindLabels,
  therapeuticRowDefinitions,
} from "cadendar-shared";
import {
  getAllowedTherapeuticMaterialOptions,
  getTherapeuticMaterialOptionByCode,
} from "cadendar-shared";
import { CCAMSearchBar } from "../../../components/CCAM/CCAMSearchBar";
import { CCAMSearchResults } from "../../../components/CCAM/CCAMSearchResults";
import { useCCAMSearch } from "../../../components/CCAM/hooks/useCCAMSearch";
import Button from "../../main/components/Button";

interface TherapeuticChoiceEditorModalProps {
  rowKey: TherapeuticChoiceRowKey;
  network: InsuranceNetwork | null;
  initialChoice: TherapeuticChoiceCombination | null;
  onClose: () => void;
  onSave: (choice: TherapeuticChoiceCombination) => void;
}

function createChoiceAct(kind: TherapeuticActKind): TherapeuticChoiceAct {
  return {
    id: crypto.randomUUID(),
    kind,
    ccamCode: "",
    patientLabel: "",
    practitionerLabel: "",
    fees: "",
    materialCode: null,
  };
}

function sortActsForRow(
  rowKey: TherapeuticChoiceRowKey,
  acts: TherapeuticChoiceAct[],
) {
  const order = getChoiceKindsForRow(rowKey);
  return [...acts].sort(
    (left, right) =>
      order.indexOf(left.kind) - order.indexOf(right.kind),
  );
}

function createInitialChoice(
  rowKey: TherapeuticChoiceRowKey,
  initialChoice: TherapeuticChoiceCombination | null,
): TherapeuticChoiceCombination {
  if (initialChoice) {
    return {
      ...initialChoice,
      acts: sortActsForRow(rowKey, initialChoice.acts),
    };
  }

  return {
    id: crypto.randomUUID(),
    acts: therapeuticRowDefinitions[rowKey].requiredKinds.map((kind) =>
      createChoiceAct(kind),
    ),
  };
}

function isValidFees(value: string) {
  const trimmed = value.trim().replace(",", ".");
  if (trimmed === "") {
    return false;
  }

  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) && parsed >= 0;
}

function formatConventionFees(tarif: number) {
  return Number.isFinite(tarif) ? tarif.toFixed(2) : "";
}

const TherapeuticChoiceEditorModal = (
  props: TherapeuticChoiceEditorModalProps,
) => {
  const [choice, setChoice] = useState<TherapeuticChoiceCombination>(() =>
    createInitialChoice(props.rowKey, props.initialChoice),
  );
  const [searchActId, setSearchActId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const searchResult = useCCAMSearch(searchQuery, { excludeExpired: true });

  const presentKinds = choice.acts.map((act) => act.kind);
  const addableKinds = therapeuticRowDefinitions[props.rowKey].optionalKinds.filter(
    (kind) =>
      canKindBeAddedToRow({ rowKey: props.rowKey, kind }) &&
      !presentKinds.includes(kind),
  );

  const updateAct = (actId: string, updater: (act: TherapeuticChoiceAct) => TherapeuticChoiceAct) => {
    setChoice((currentChoice) => ({
      ...currentChoice,
      acts: currentChoice.acts.map((act) =>
        act.id === actId ? updater(act) : act,
      ),
    }));
  };

  const handleAddKind = (kind: TherapeuticActKind) => {
    setChoice((currentChoice) => ({
      ...currentChoice,
      acts: sortActsForRow(props.rowKey, [
        ...currentChoice.acts,
        createChoiceAct(kind),
      ]),
    }));
  };

  const handleRemoveKind = (actId: string) => {
    setChoice((currentChoice) => ({
      ...currentChoice,
      acts: currentChoice.acts.filter((act) => act.id !== actId),
    }));
  };

  const handleSelectActe = (actId: string, acte: CCAMActe) => {
    updateAct(actId, (act) => ({
      ...act,
      ccamCode: acte.code,
      patientLabel: act.patientLabel.trim() === "" ? acte.libelle : act.patientLabel,
      practitionerLabel:
        act.practitionerLabel.trim() === "" ? acte.libelle : act.practitionerLabel,
      fees: formatConventionFees(acte.tarif),
    }));
    setSearchActId(null);
    setSearchQuery("");
  };

  const handleSubmit = () => {
    if (choice.acts.length === 0) {
      setFormError("Ajoutez au moins un acte.");
      return;
    }

    for (const act of choice.acts) {
      if (act.ccamCode.trim() === "") {
        setFormError(`Le code CCAM est requis pour ${therapeuticActKindLabels[act.kind]}.`);
        return;
      }
      if (act.patientLabel.trim() === "") {
        setFormError(`L'intitulé patient est requis pour ${therapeuticActKindLabels[act.kind]}.`);
        return;
      }
      if (act.practitionerLabel.trim() === "") {
        setFormError(
          `L'intitulé praticien est requis pour ${therapeuticActKindLabels[act.kind]}.`,
        );
        return;
      }
      if (!isValidFees(act.fees)) {
        setFormError(`Les honoraires sont invalides pour ${therapeuticActKindLabels[act.kind]}.`);
        return;
      }

      const family = getTherapeuticMaterialFamilyForActKind(act.kind);
      if (family && act.materialCode === null) {
        setFormError(`Le matériau est requis pour ${therapeuticActKindLabels[act.kind]}.`);
        return;
      }
    }

    setFormError(null);
    props.onSave(choice);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-6">
      <div className="w-full max-w-5xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-950">
              {therapeuticRowDefinitions[props.rowKey].label}
            </h2>
            <p className="text-sm text-slate-500">
              Configurez une combinaison d'actes pour ce choix.
            </p>
          </div>
          <Button title="Fermer" onClick={props.onClose} />
        </div>

        <div className="space-y-6 px-6 py-6">
          {choice.acts.map((act) => {
            const family = getTherapeuticMaterialFamilyForActKind(act.kind);
            const materialOptions = family
              ? getAllowedTherapeuticMaterialOptions({
                  family,
                  network: props.network,
                })
              : [];
            const isRequired = isRequiredKindForRow({
              rowKey: props.rowKey,
              kind: act.kind,
            });
            const selectedMaterial =
              act.materialCode !== null
                ? getTherapeuticMaterialOptionByCode(act.materialCode)
                : null;

            return (
              <div key={act.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      {therapeuticActKindLabels[act.kind]}
                    </div>
                    <div className="text-xs text-slate-500">
                      {act.ccamCode.trim() === "" ? "Aucun code sélectionné" : act.ccamCode}
                    </div>
                  </div>
                  {!isRequired ? (
                    <Button
                      title="Retirer"
                      onClick={() => handleRemoveKind(act.id)}
                      style="danger"
                    />
                  ) : null}
                </div>

                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  <label className="block text-sm text-slate-700">
                    <span className="mb-1 block font-medium">Intitulé patient</span>
                    <input
                      type="text"
                      value={act.patientLabel}
                      onChange={(event) => {
                        updateAct(act.id, (currentAct) => ({
                          ...currentAct,
                          patientLabel: event.target.value,
                        }));
                      }}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 outline-none focus:border-blue-600"
                    />
                  </label>

                  <label className="block text-sm text-slate-700">
                    <span className="mb-1 block font-medium">Intitulé praticien</span>
                    <input
                      type="text"
                      value={act.practitionerLabel}
                      onChange={(event) => {
                        updateAct(act.id, (currentAct) => ({
                          ...currentAct,
                          practitionerLabel: event.target.value,
                        }));
                      }}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 outline-none focus:border-blue-600"
                    />
                  </label>

                  <label className="block text-sm text-slate-700">
                    <span className="mb-1 block font-medium">Honoraires</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={act.fees}
                      onChange={(event) => {
                        updateAct(act.id, (currentAct) => ({
                          ...currentAct,
                          fees: event.target.value,
                        }));
                      }}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 outline-none focus:border-blue-600"
                    />
                  </label>

                  {family ? (
                    <label className="block text-sm text-slate-700">
                      <span className="mb-1 block font-medium">Matériau</span>
                      <select
                        value={act.materialCode ?? ""}
                        onChange={(event) => {
                          const nextMaterial = materialOptions.find(
                            (option) => option.code === event.target.value,
                          );

                          updateAct(act.id, (currentAct) => ({
                            ...currentAct,
                            materialCode:
                              event.target.value.trim() === ""
                                ? null
                                : nextMaterial?.code ?? null,
                          }));
                        }}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 outline-none focus:border-blue-600"
                      >
                        <option value="">Choisir un matériau</option>
                        {materialOptions.map((option) => (
                          <option key={option.code} value={option.code}>
                            {option.selectLabel}
                          </option>
                        ))}
                      </select>
                      {selectedMaterial && act.materialCode !== null ? (
                        <div className="mt-1 text-xs text-slate-500">
                          Sélection actuelle : {selectedMaterial.selectLabel}
                        </div>
                      ) : null}
                    </label>
                  ) : (
                    <div className="rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2 text-sm text-slate-500">
                      Aucun matériau spécifique pour cet acte.
                    </div>
                  )}
                </div>

                <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium text-slate-900">
                        Code CCAM
                      </div>
                      <div className="text-sm text-slate-500">
                        {act.ccamCode.trim() === "" ? "Aucun code sélectionné" : act.ccamCode}
                      </div>
                    </div>
                    <Button
                      title={searchActId === act.id ? "Fermer la recherche" : "Choisir un code CCAM"}
                      onClick={() => {
                        setSearchActId(searchActId === act.id ? null : act.id);
                        setSearchQuery("");
                      }}
                      style="secondary"
                    />
                  </div>

                  {searchActId === act.id ? (
                    <div className="mt-4">
                      <CCAMSearchBar
                        onSearchChange={setSearchQuery}
                        filters={{ excludeExpired: true }}
                        isLoading={searchResult.isLoading}
                      />
                      <CCAMSearchResults
                        results={searchResult.data?.actes || []}
                        onSelectActe={(acte) => handleSelectActe(act.id, acte)}
                        isLoading={searchResult.isLoading}
                        query={searchQuery}
                        hasMore={searchResult.hasMore}
                        onLoadMore={searchResult.loadMore}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}

          {addableKinds.length > 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 p-4">
              <div className="text-sm font-medium text-slate-900">
                Ajouter un acte optionnel
              </div>
              <div className="mt-3 flex flex-wrap gap-3">
                {addableKinds.map((kind) => (
                  <Button
                    key={kind}
                    title={therapeuticActKindLabels[kind]}
                    onClick={() => handleAddKind(kind)}
                    style="secondary"
                  />
                ))}
              </div>
            </div>
          ) : null}

          {formError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
              {formError}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <Button title="Annuler" onClick={props.onClose} />
          <Button title="Enregistrer le choix" onClick={handleSubmit} style="main" />
        </div>
      </div>
    </div>
  );
};

export default TherapeuticChoiceEditorModal;
